import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Button, CircularProgress, Stack } from "@mui/material";

import {
    getLeadGeneralReport,
    LeadReportResponseData,
} from "helpers/leads/generalReports";

import SwalAlertComp from "components/SwalAlert";

import PageContainer from "../../components/atoms/PageContainer";
import usePrev from "../../hooks/usePrev";
import AggregatedView from "./components/AggregatedView";
import ConnectionView from "./components/ConnectionView";
import EmptyState from "./components/EmptyState";
import FilterBar from "./components/FilterBar";
import SummaryCards from "./components/SummaryCards";
import {
    parseLeadReport,
    resolveDateRange,
    validateRange,
} from "./LeadGeneralReportsPage.helpers";
import * as Styled from "./LeadGeneralReportsPage.styled";
import {
    DEFAULT_FILTERS,
    LeadGeneralReportData,
    LeadGeneralReportFilters,
} from "./LeadGeneralReportsPage.types";

const LeadGeneralReportsPage: React.FC = () => {
    const [filters, setFilters] =
        useState<LeadGeneralReportFilters>(DEFAULT_FILTERS);
    const [pendingFilters, setPendingFilters] =
        useState<LeadGeneralReportFilters>(DEFAULT_FILTERS);
    const [data, setData] = useState<LeadGeneralReportData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [connectionOptions, setConnectionOptions] = useState<
        { id: number; name: string }[]
    >([]);
    const hasSelectedAllConnections = useRef(false);

    const prevGroupBy = usePrev(filters.groupBy);

    const showWarning = useCallback((message: string) => {
        SwalAlertComp({
            title: "Warning",
            message,
            icon: "warning",
        });
    }, []);

    const fetchReport = useCallback(
        async (applied: LeadGeneralReportFilters) => {
            const validation = validateRange(applied);
            if (!validation.ok) {
                showWarning(validation.message ?? "Invalid date range.");
                return;
            }

            setIsLoading(true);
            setError(null);
            const { start, end } = resolveDateRange(applied);

            const response = await getLeadGeneralReport({
                start_date: start,
                end_date: end,
                group_by: applied.groupBy,
                ...(applied.statuses.length
                    ? { statuses: applied.statuses }
                    : {}),
                ...(applied.connectionIds?.length
                    ? { connection_ids: applied.connectionIds }
                    : {}),
            });

            if (response.status) {
                const parsed = parseLeadReport(
                    response.data as LeadReportResponseData,
                );
                setData(parsed);
                if (parsed?.connections?.length) {
                    setConnectionOptions((prev) => {
                        const merged = new Map(
                            prev.map((option) => [option.id, option]),
                        );
                        parsed.connections!.forEach((connection) => {
                            merged.set(connection.id, {
                                id: connection.id,
                                name: connection.connectionName,
                            });
                        });
                        return Array.from(merged.values()).sort((a, b) =>
                            a.name.localeCompare(b.name),
                        );
                    });
                }
            } else {
                const message =
                    response.message ||
                    "Something went wrong while loading the report.";
                setError(message);
                setData(null);
                SwalAlertComp({
                    title: "Error",
                    message,
                    icon: "error",
                });
            }
            setIsLoading(false);
        },
        [showWarning],
    );

    useEffect(() => {
        fetchReport(DEFAULT_FILTERS);
    }, [fetchReport]);

    // The connection list only exists once a report has been loaded, so the
    // "all selected" default is applied the first time options arrive. No
    // refetch is needed: selecting every connection matches the initial
    // unfiltered request.
    useEffect(() => {
        if (hasSelectedAllConnections.current || !connectionOptions.length) {
            return;
        }
        hasSelectedAllConnections.current = true;
        const allIds = connectionOptions.map((option) => option.id);
        setFilters((prev) => ({ ...prev, connectionIds: allIds }));
        setPendingFilters((prev) => ({ ...prev, connectionIds: allIds }));
    }, [connectionOptions]);

    const resetFilters = useCallback(() => {
        const next: LeadGeneralReportFilters = {
            ...DEFAULT_FILTERS,
            statuses: [...DEFAULT_FILTERS.statuses],
            connectionIds: connectionOptions.length
                ? connectionOptions.map((option) => option.id)
                : undefined,
        };
        setFilters(next);
        setPendingFilters(next);
        fetchReport(next);
    }, [connectionOptions, fetchReport]);

    const handleUpdate = () => {
        const validation = validateRange(pendingFilters);
        if (!validation.ok) {
            showWarning(validation.message ?? "Invalid date range.");
            return;
        }
        setFilters(pendingFilters);
        fetchReport(pendingFilters);
    };

    const isEmpty = useMemo(() => {
        if (!data) return false;
        return (data.summary?.totalLeads ?? 0) === 0;
    }, [data]);

    const renderMainContent = () => {
        if ((isLoading && !data) || prevGroupBy !== filters.groupBy) {
            return (
                <Styled.SectionCard elevation={0}>
                    <Box
                        sx={{
                            minHeight: 320,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            gap: 1.5,
                        }}
                    >
                        <CircularProgress sx={{ color: "#ED6C02" }} size={32} />
                        <Box sx={{ fontSize: 13, color: "#64748B" }}>
                            Preparing report...
                        </Box>
                    </Box>
                </Styled.SectionCard>
            );
        }

        if (!data) return null;

        if (isEmpty) {
            return (
                <EmptyState onClear={resetFilters} />
            );
        }

        if (filters.groupBy === "connection") {
            return (
                <ConnectionView
                    daily={data.daily ?? []}
                    rows={data.connections ?? []}
                />
            );
        }

        return (
            <AggregatedView
                timeseries={data.daily ?? []}
                sources={data.sourceBreakdown ?? []}
            />
        );
    };

    return (
        <PageContainer
            showBackButton={false}
            title="Lead General Reports"
            subtitle="Real-time performance and routing analytics across all lead connections."
        >
            <Box mb={3}>
                <FilterBar
                    filters={pendingFilters}
                    onChange={setPendingFilters}
                    onUpdate={handleUpdate}
                    isLoading={isLoading}
                    connectionOptions={connectionOptions}
                />
            </Box>

            {error && (
                <Box mb={3}>
                    <Styled.ErrorBanner>
                        <Stack direction="row" gap={1} alignItems="center">
                            <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                            Something went wrong while loading the report.
                        </Stack>
                        <Button
                            color="primary"
                            onClick={() => fetchReport(filters)}
                            size="small"
                            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                            variant="contained"
                        >
                            Try Again
                        </Button>
                    </Styled.ErrorBanner>
                </Box>
            )}

            <Box mb={3}>
                {isLoading && !data ? (
                    <SummaryCards
                        isLoading
                        metrics={{
                            totalLeads: 0,
                            sent: 0,
                            failed: 0,
                            duplicated: 0,
                            sentSuccessRate: 0,
                            failedAnomalyPct: 0,
                            duplicatedRate: 0,
                        }}
                    />
                ) : (
                    data && <SummaryCards metrics={data.summary} />
                )}
            </Box>

            {renderMainContent()}
        </PageContainer>
    );
};

export default LeadGeneralReportsPage;
